// Lib
import Handsontable from 'handsontable';
import { Events } from 'handsontable/pluginHooks';
import { useCallback, useEffect } from 'react';
import { useHotTableDispatch } from '../store/hotTableStoreHooks';
import { setActiveTable } from '../store/hotTableStoreActions';
import rafThrottle from '../../../../common/utils/lib/rafThrottle';
import { TableDataVersion } from '../../../../common/table/TableTypes';
import { ActiveTableObject, ActiveTableSession } from '../store/hotTableStoreTypes';

const TRACKED_HOOKS: (keyof Events)[] = [
    'afterChange',
    'afterCreateRow',
    'afterCreateCol',
    'afterRemoveRow',
    'afterRemoveCol',
    'afterRowMove',
    'afterColumnMove',
    'afterColumnResize',
    'afterSelection',
    'afterSelectionEnd',
    'afterDeselect',
    'afterSetCellMeta',
];

const getActiveTableStoreEntry = ({
    elementId,
    tableInstance,
    session,
}: {
    elementId: string;
    tableInstance: Handsontable;
    session: ActiveTableObject['session'];
}): ActiveTableObject => ({
    elementId,
    instance: tableInstance,
    tableDataVersion: TableDataVersion.V2,
    session,
});

/**
 * Stores the current active Handsontable instance in the HotTable store & clears it when no longer edited.
 */
export const useHotStoreActiveTableSync = ({
    tableInstance,
    elementId,
    isActive,
    session,
}: {
    tableInstance: Handsontable | null;
    elementId: string;
    isActive: boolean;
    session: ActiveTableSession;
}): void => {
    const hotTableDispatch = useHotTableDispatch();
    const dispatchUpdateActiveTable = useCallback(
        (activeTable: ActiveTableObject | null) => hotTableDispatch(setActiveTable(activeTable)),
        [hotTableDispatch],
    );

    useEffect(() => {
        if (!tableInstance) return;
        if (!isActive) return;

        dispatchUpdateActiveTable(getActiveTableStoreEntry({ elementId, tableInstance, session }));

        const throttledUpdateActiveTable = rafThrottle(() =>
            dispatchUpdateActiveTable(getActiveTableStoreEntry({ elementId, tableInstance, session })),
        );

        TRACKED_HOOKS.forEach((hook) => tableInstance.addHook(hook, throttledUpdateActiveTable));

        return () => {
            // You shouldn't be able to immediately switch from editing one table to another, or edit
            //  two tables at once, so this should be a safe operation (i.e. won't clobber another active table).
            dispatchUpdateActiveTable(null);

            throttledUpdateActiveTable.cancel();

            TRACKED_HOOKS.forEach((hook) => tableInstance.removeHook(hook, throttledUpdateActiveTable));
        };
    }, [dispatchUpdateActiveTable, elementId, isActive, session, tableInstance]);
};
